import "common/Charts/chartRegistry";
import { DISPLAY_CURRENCIES } from "common/constants";
import { renderToString } from "react-dom/server.browser";
import { BTC } from "common/Icons/Icons";
import { CSSProperties, useContext, useMemo, useRef } from "react";
import { Bar } from "react-chartjs-2";
import type { Chart, ChartOptions, TooltipModel } from "chart.js";
import LoadingSpinnerOverlay, {
  LoadingSpinnerOverlayProps,
} from "common/Overlays/LoadingSpinnerOverlay";
import {
  getTooltipGenerator,
  lineChartTooltipInnerHtmlGenerator,
} from "common/Charts/tooltipGenerator";
import { barChartHoverPlugin, getTimeChartOptions } from "utils/charts";
import { useTheme } from "styled-components";
import { v4 as uuid } from "uuid";
import useMeasure from "utils/hooks/useMeasure";
import { hexToRGB } from "@shared/utils/misc";
import { showListQueryEmptyState } from "@shared/common/utils";
import ChartEmpty from "common/Charts/ChartEmpty";
import { DisplayCurrency, FormattedChartDataPoint } from "@shared/types";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import { BAR_CHART_ID } from "testIds.ts";

interface BarChartProps {
  data?: FormattedChartDataPoint[] | StackedBarChartData[];
  loading?: boolean;
  loadingProps?: LoadingSpinnerOverlayProps;
  displayCurrency?: DisplayCurrency;
  usePositiveNegativeColorScheme?: boolean;
  precision?: number;
  isSuccess?: boolean;
  isStacked?: boolean;
  xMin?: string;
  xMax?: string;
  loadingBgColor?: string;
  hideXAxis?: boolean;
  hideYAxis?: boolean;
  ticksColor?: string;
  numOfXTicks?: number;
  numOfYTicks?: number;
  hideTooltip?: boolean;
  gridColor?: string;
  positiveColor?: string;
  negativeColor?: string;
  style?: CSSProperties;
  axisOverlapData?: boolean;
  color?: string;
  height?: number;
  /** Final say over the chart.js options (e.g. a page's own axis style). */
  adjustOptions?: (options: ChartOptions<"bar">) => ChartOptions<"bar">;
  /**
   * Custom tooltip body (an HTML string: escape any data in it, see
   * utils/html); also enables the tooltip on stacked charts.
   */
  tooltipHtml?: (context: BarChartTooltipContext) => string;
}

/** What chart.js hands an external tooltip. */
export interface BarChartTooltipContext {
  chart: Chart;
  tooltip: TooltipModel<"bar">;
}

const BarChart = ({
  data,
  loading,
  loadingProps,
  displayCurrency,
  usePositiveNegativeColorScheme,
  precision,
  isSuccess,
  isStacked,
  xMin,
  xMax,
  loadingBgColor,
  hideXAxis,
  hideYAxis,
  ticksColor,
  numOfXTicks,
  numOfYTicks,
  hideTooltip,
  gridColor,
  positiveColor,
  negativeColor,
  style,
  axisOverlapData,
  color,
  height: propsHeight,
  adjustOptions,
  tooltipHtml,
}: BarChartProps) => {
  const { isDrawer } = useContext(DrawerContext) || {};
  const chartUuid = useRef(uuid()).current;
  const appTheme = useTheme();
  const [measureRef, { width, height: measureHeight }] =
    useMeasure<HTMLDivElement>();
  const height = propsHeight ?? measureHeight;

  const currencyIcon = displayCurrency
    ? displayCurrency === DISPLAY_CURRENCIES.BTC
      ? renderToString(<BTC />)
      : "$"
    : "";

  const absoluteValueData = useMemo(() => {
    if (usePositiveNegativeColorScheme && !isStacked) {
      return data?.map((dataPoint) => ({
        ...dataPoint,
        y: Math.abs(dataPoint.y),
        isNegative: dataPoint.y < 0,
      }));
    }
    return data;
  }, [data, usePositiveNegativeColorScheme]);

  const getBackgroundAndBorderColors = () => {
    let borderColor, backgroundColor;
    if (usePositiveNegativeColorScheme) {
      if (positiveColor && negativeColor) {
        const color = data?.map((dataPoint) =>
          dataPoint.y >= 0 ? positiveColor : negativeColor
        );
        borderColor = color;
        backgroundColor = color;
      } else {
        const alpha = 0.4;
        borderColor = data?.map((dataPoint) =>
          dataPoint.y >= 0 ? appTheme.colors.green : appTheme.colors.red
        );
        backgroundColor = data?.map((dataPoint) =>
          dataPoint.y >= 0
            ? hexToRGB(appTheme.colors.green, alpha)
            : hexToRGB(appTheme.colors.red, alpha)
        );
      }
    } else {
      borderColor = color ?? appTheme.colors.primary;
      backgroundColor = hexToRGB(color ?? appTheme.colors.primary, 0.4);
    }
    return [backgroundColor, borderColor];
  };

  const [backgroundColor, borderColor] = getBackgroundAndBorderColors();
  const getChartData = () => {
    let datasets: any[];
    if (isStacked) {
      datasets = data || [];
    } else {
      datasets = [
        {
          label: "First dataset",
          data: absoluteValueData,
          backgroundColor: backgroundColor,
          fill: true,
          borderColor: borderColor,
          borderWidth: 1,
          hoverBackgroundColor: borderColor,
        },
      ];
    }
    return {
      datasets,
    };
  };

  const chartParentElementId = chartUuid + "_chart_parent";
  const tooltipGenerator = getTooltipGenerator(
    chartUuid + "_tooltip",
    tooltipHtml ?? lineChartTooltipInnerHtmlGenerator(currencyIcon),
    true,
    10,
    chartParentElementId
  );

  const showEmptyState =
    !xMax && showListQueryEmptyState(data, loading, isSuccess);

  const chart = useMemo(() => {
    if (showEmptyState) {
      return <ChartEmpty />;
    } else {
      return (
        <Bar
          style={{ width, height, ...style }}
          data={getChartData()}
          // @ts-ignore
          options={applyAdjustOptions(
            adjustOptions,
            withNewLookBarAxis(
              appTheme.isNewLook,
              getTimeChartOptions(
                isStacked && !tooltipHtml ? undefined : tooltipGenerator,
                precision,
                null,
                null,
                absoluteValueData,
                undefined,
                undefined,
                appTheme,
                undefined,
                numOfXTicks,
                isStacked,
                false,
                xMin,
                xMax,
                undefined,
                hideXAxis,
                hideYAxis,
                ticksColor,
                numOfYTicks,
                undefined,
                hideTooltip,
                axisOverlapData,
                gridColor
              )
            )
          )}
          plugins={[barChartHoverPlugin]}
        />
      );
    }
  }, [
    absoluteValueData,
    precision,
    showEmptyState,
    hideXAxis,
    hideYAxis,
    ticksColor,
    numOfXTicks,
    numOfYTicks,
    hideTooltip,
    gridColor,
    axisOverlapData,
    xMin,
    xMax,
  ]);

  return (
    <div
      data-testid={BAR_CHART_ID}
      style={{ position: "relative", width: "100%", height: "100%" }}
      ref={measureRef}
      id={chartParentElementId}
    >
      <LoadingSpinnerOverlay
        isLoading={loading}
        overlayOpacity={0.7}
        backgroundColor={
          loadingBgColor ??
          appTheme.colors[isDrawer ? "layoutBgColor" : "cardBgColor"]
        }
        {...loadingProps}
      >
        {height && width && chart}
      </LoadingSpinnerOverlay>
    </div>
  );
};

export default BarChart;

interface StackedBarChartData {
  label?: string;
  data: FormattedChartDataPoint[];
  backgroundColor?: string;
}

// Bars read against zero: the new look's value axis always starts there.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const withNewLookBarAxis = (isNewLook: boolean, options: any) => {
  if (!isNewLook || !options?.scales?.y) return options;
  const { y } = options.scales;
  // The options set an explicit minimum from the data range; bars start at 0.
  const min = typeof y.min === "number" && y.min > 0 ? 0 : y.min;
  return {
    ...options,
    scales: { ...options.scales, y: { ...y, min, beginAtZero: true } },
  };
};

const applyAdjustOptions = (
  adjustOptions: BarChartProps["adjustOptions"],
  options: ChartOptions<"bar">
) => (adjustOptions ? adjustOptions(options) : options);
